/**
 * useAppUiSettings — the app-wide look (`theme_preset`) and `density`, from
 * de-hub's shared App Support module, plus an admin's own preview of another
 * look.
 *
 * Takes the auth pieces as arguments rather than from AppContext because its
 * caller is AppContextProvider itself, which draws the whole app with the
 * result and so cannot read its own context.
 *
 * FIRST PAINT USES THE LAST KNOWN SETTINGS. They are remembered per app in
 * this browser, so a user whose app is on Ledger does not see Classic flash
 * up on every page load before the GET returns. With nothing remembered, and
 * whenever loading fails, it is `classic` / `comfortable` — the app as it
 * looked before either existed — so a de-hub outage can never leave the app
 * half-themed. A failed load is reported (`loadError`, `reload`) rather than
 * passed off as "the setting is Classic".
 *
 * THE PREVIEW IS ADMIN-ONLY. It is a tool for trying a look before applying
 * it, so it is ignored for anyone who is not an admin — otherwise someone who
 * lost the role would be stuck in a look nobody else sees, with no way back
 * to the screen that clears it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  normalizeThemeDensity,
  normalizeThemePreset,
  useWigThemePreview,
} from "@warwick-investment-group/wig-react-library";

import appSupportService from "../services/appSupportService";
import { APP_KEY } from "../config/appSupport";

const LAST_KNOWN_KEY = `wig-ui-settings:${APP_KEY}`;

const readLastKnown = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LAST_KNOWN_KEY) || "{}");
    return {
      themePreset: normalizeThemePreset(parsed?.theme_preset),
      density: normalizeThemeDensity(parsed?.density),
    };
  } catch {
    return { themePreset: "classic", density: "comfortable" };
  }
};

const writeLastKnown = (data) => {
  try {
    localStorage.setItem(
      LAST_KNOWN_KEY,
      JSON.stringify({ theme_preset: data?.theme_preset, density: data?.density })
    );
  } catch {
    // Storage blocked: the next page load just starts from Classic.
  }
};

export const useAppUiSettings = ({ token, getToken, isTokenExpired, user, isAdmin }) => {
  const [initial] = useState(readLastKnown);
  const [themePreset, setThemePreset] = useState(initial.themePreset);
  const [density, setDensity] = useState(initial.density);
  const [canEditTheme, setCanEditTheme] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [themePreview, setThemePreview] = useWigThemePreview(APP_KEY);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  // Read through a ref so the load runs when the TOKEN changes, not every
  // time AppContextProvider rebuilds `user` or `getToken` around it.
  const getAuthRef = useRef(getAuth);
  useEffect(() => {
    getAuthRef.current = getAuth;
  }, [getAuth]);

  // Bumped by every save. A load that started before a save must not land
  // after it and put the old value back.
  const writeVersion = useRef(0);

  const accept = useCallback((resp) => {
    const data = resp?.data?.data;
    setThemePreset(normalizeThemePreset(data?.theme_preset));
    setDensity(normalizeThemeDensity(data?.density));
    setCanEditTheme(Boolean(resp?.data?.can_edit));
    writeLastKnown(data);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const startedAt = writeVersion.current;
    (async () => {
      try {
        const { token: tk, userOid } = await getAuthRef.current();
        if (!tk) return;
        const resp = await appSupportService.getUiSettings({ token: tk, userOid });
        if (cancelled || writeVersion.current !== startedAt) return;
        accept(resp);
        setLoadError(false);
      } catch {
        // Keep what is on screen; see the header.
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey, accept]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Admin only (the server refuses everyone else). Throws on failure.
  const applySetting = useCallback(
    async (key, value) => {
      writeVersion.current += 1;
      const { token: tk, userOid } = await getAuthRef.current();
      const resp = await appSupportService.updateUiSetting({
        token: tk,
        userOid,
        key,
        value,
      });
      accept(resp);
      setLoadError(false);
    },
    [accept]
  );
  const applyThemePreset = useCallback(
    (preset) => applySetting("theme_preset", preset),
    [applySetting]
  );
  const applyDensity = useCallback(
    (next) => applySetting("density", next),
    [applySetting]
  );

  const activePreview = isAdmin ? themePreview : null;

  return {
    themePreset,
    canEditTheme,
    applyThemePreset,
    density,
    applyDensity,
    loadError,
    reload,
    themePreview: activePreview,
    setThemePreview,
    // What the app is actually drawn with in this browser.
    effectiveThemePreset: activePreview || themePreset,
  };
};

export default useAppUiSettings;
